import { ContentRegistry, loadContentRegistry } from '../data';
import {
  addUnlockedWeapon,
  commitRunSummary,
  createDefaultProfile,
  PlayerProfile,
  ProfileSettings,
  RunSummary,
  updateProfileSettings,
  normalizeProfile
} from '../data/ProfileState';
import { createLogger, Logger } from '../utils/Logger';

export interface ProfileStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type ProfileResult<T> =
  | { ok: true; profile: PlayerProfile; value: T; created?: boolean; migrated?: boolean }
  | { ok: false; profile: PlayerProfile; error: string; reason: string };

export type ProfileSnapshotResult = { ok: true; profile: PlayerProfile } | { ok: false; error: string; profile?: PlayerProfile };

export interface ProfileStorageManagerOptions {
  readonly storage?: ProfileStorageLike | null;
  readonly storageKey?: string;
  readonly contentRegistry?: ContentRegistry;
  readonly logger?: Logger;
}

export interface ResolvedWeaponUnlocks {
  readonly starterWeaponIds: readonly string[];
  readonly availableWeaponIds: readonly string[];
  readonly invalidWeaponIds: readonly string[];
}

const DEFAULT_PROFILE_KEY = 'phaser-game.profile.v1';
const LEGACY_MUSIC_VOLUME_KEY = 'audio.musicVolume';
const LEGACY_SFX_VOLUME_KEY = 'audio.sfxVolume';
const LEGACY_MUTE_KEY = 'audio.mute';

export class ProfileStorageManager {
  private readonly storage: ProfileStorageLike | null;
  private readonly storageKey: string;
  private readonly contentRegistry: ContentRegistry;
  private readonly logger: Logger;
  private cachedProfile: PlayerProfile | null = null;

  constructor(options: ProfileStorageManagerOptions = {}) {
    this.storage = options.storage === undefined ? resolveLocalStorage() : options.storage;
    this.storageKey = options.storageKey ?? DEFAULT_PROFILE_KEY;
    this.contentRegistry = options.contentRegistry ?? loadContentRegistry();
    this.logger = options.logger ?? createLogger('ProfileStorageManager');
    this.logger.debug('constructor:complete', { storageKey: this.storageKey, hasStorage: !!this.storage });
  }

  public loadProfile(): ProfileResult<PlayerProfile> {
    this.logger.debug('loadProfile:start', { storageKey: this.storageKey });
    const defaultWeaponIds = this.contentRegistry.getStarterWeaponIds();
    const defaults = createDefaultProfile(defaultWeaponIds, this.logger);

    if (!this.storage) {
      this.cachedProfile = defaults;
      this.logger.warn('loadProfile:storage-unavailable-defaults-used', { storageKey: this.storageKey });
      return { ok: false, profile: defaults, error: 'storage-unavailable', reason: 'missing-localStorage' };
    }

    try {
      const raw = this.storage.getItem(this.storageKey);
      if (!raw) {
        const migration = this.migrateLegacyAudioSettings(defaults);
        this.cachedProfile = migration.profile;
        const wroteProfile = this.tryWrite(migration.profile, 'loadProfile:create');
        if (migration.migrated && wroteProfile) {
          this.clearLegacyAudioSettings('loadProfile:create');
        }
        this.logger.info('loadProfile:created-default-profile', { storageKey: this.storageKey });
        return { ok: true, profile: migration.profile, value: migration.profile, created: true };
      }

      const parsed = JSON.parse(raw) as unknown;
      const migration = this.migrateLegacyAudioSettings(normalizeProfile(parsed, { defaultWeaponIds, logger: this.logger }));
      const serializedProfile = JSON.stringify(migration.profile);
      if (migration.migrated || serializedProfile !== raw) {
        const wroteProfile = this.tryWrite(migration.profile, migration.migrated ? 'loadProfile:migrate-legacy-audio' : 'loadProfile:normalize');
        if (migration.migrated && wroteProfile) {
          this.clearLegacyAudioSettings('loadProfile:migrate-legacy-audio');
        }
      }
      this.cachedProfile = migration.profile;
      this.logger.debug('loadProfile:complete', { storageKey: this.storageKey, schemaVersion: migration.profile.schemaVersion });
      return { ok: true, profile: migration.profile, value: migration.profile, migrated: migration.migrated };
    } catch (error) {
      this.cachedProfile = defaults;
      this.logger.warn('loadProfile:invalid-payload-defaults-used', { storageKey: this.storageKey, error });
      return { ok: false, profile: defaults, error: 'invalid-profile-payload', reason: String(error) };
    }
  }

  public getProfile(): PlayerProfile {
    return this.cachedProfile ?? this.loadProfile().profile;
  }

  public getSnapshot(): ProfileSnapshotResult {
    this.logger.debug('getSnapshot:start', { storageKey: this.storageKey });
    const result = this.loadProfile();
    if (!result.ok) {
      return { ok: false, error: result.error, profile: result.profile };
    }
    return { ok: true, profile: result.profile };
  }

  public saveProfile(profile: PlayerProfile): ProfileResult<PlayerProfile> {
    const normalized = normalizeProfile(profile, {
      defaultWeaponIds: this.contentRegistry.getStarterWeaponIds(),
      logger: this.logger
    });
    this.logger.debug('saveProfile:start', { storageKey: this.storageKey, schemaVersion: normalized.schemaVersion });
    if (!this.storage) {
      this.cachedProfile = normalized;
      this.logger.warn('saveProfile:storage-unavailable', { storageKey: this.storageKey });
      return { ok: false, profile: normalized, error: 'storage-unavailable', reason: 'missing-localStorage' };
    }

    try {
      this.storage.setItem(this.storageKey, JSON.stringify(normalized));
      this.cachedProfile = normalized;
      this.logger.debug('saveProfile:complete', { storageKey: this.storageKey });
      return { ok: true, profile: normalized, value: normalized };
    } catch (error) {
      this.cachedProfile = normalized;
      this.logger.error('saveProfile:write-failed', { storageKey: this.storageKey, operation: 'setItem', error });
      return { ok: false, profile: normalized, error: 'write-failed', reason: String(error) };
    }
  }

  public clearProfile(): ProfileSnapshotResult {
    this.logger.debug('clearProfile:start', { storageKey: this.storageKey });
    const defaults = createDefaultProfile(this.contentRegistry.getStarterWeaponIds(), this.logger);
    this.cachedProfile = defaults;
    if (!this.storage) {
      this.logger.warn('clearProfile:storage-unavailable-defaults-used', { storageKey: this.storageKey });
      return { ok: false, error: 'storage-unavailable', profile: defaults };
    }
    try {
      this.storage.removeItem(this.storageKey);
      this.storage.removeItem(LEGACY_MUSIC_VOLUME_KEY);
      this.storage.removeItem(LEGACY_SFX_VOLUME_KEY);
      this.storage.removeItem(LEGACY_MUTE_KEY);
      this.logger.debug('clearProfile:complete', { storageKey: this.storageKey });
      return { ok: true, profile: defaults };
    } catch (error) {
      this.logger.error('clearProfile:write-failed', { storageKey: this.storageKey, operation: 'removeItem', error });
      return { ok: false, error: 'write-failed', profile: defaults };
    }
  }

  public updateSettings(patch: Partial<ProfileSettings>): ProfileResult<PlayerProfile> {
    this.logger.debug('updateSettings:start', { keys: Object.keys(patch) });
    return this.saveProfile(updateProfileSettings(this.getProfile(), patch, this.logger));
  }

  public commitRun(summary: RunSummary): ProfileResult<RunSummary> {
    const result = commitRunSummary(this.getProfile(), summary, this.logger);
    const saved = this.saveProfile(result.profile);
    if (!saved.ok) {
      return { ok: false, profile: saved.profile, error: saved.error, reason: saved.reason };
    }
    this.logger.info('commitRun:summary-committed', { summary, deltas: result.deltas });
    return { ok: true, profile: saved.profile, value: summary };
  }

  public unlockWeapon(weaponId: string): ProfileResult<string> {
    const result = addUnlockedWeapon(this.getProfile(), weaponId, this.logger);
    if (!result.changed) {
      return { ok: true, profile: result.profile, value: weaponId };
    }
    const saved = this.saveProfile(result.profile);
    if (!saved.ok) {
      return { ok: false, profile: saved.profile, error: saved.error, reason: saved.reason };
    }
    return { ok: true, profile: saved.profile, value: weaponId };
  }

  public resolveWeaponUnlocks(): ResolvedWeaponUnlocks {
    const profile = this.getProfile();
    const contentAvailable = this.contentRegistry.getAvailableWeaponIds();
    const starter = this.contentRegistry.getStarterWeaponIds();
    const unlocked = new Set([...starter, ...profile.unlocks.weaponIds]);
    const invalidWeaponIds = [...unlocked].filter(id => !this.contentRegistry.hasWeapon(id));
    const availableWeaponIds = contentAvailable.filter(id => this.contentRegistry.hasWeapon(id));
    const starterWeaponIds = starter.filter(id => unlocked.has(id) && this.contentRegistry.hasWeapon(id));
    if (invalidWeaponIds.length > 0) {
      this.logger.warn('resolveWeaponUnlocks:invalid-ids-filtered', { invalidWeaponIds });
    }
    this.logger.info('resolveWeaponUnlocks:runtime-pools-resolved', {
      starterWeaponIds,
      availableWeaponIds,
      persistedUnlockIds: profile.unlocks.weaponIds,
      invalidWeaponIds
    });
    return { starterWeaponIds, availableWeaponIds, invalidWeaponIds };
  }

  private migrateLegacyAudioSettings(profile: PlayerProfile): { profile: PlayerProfile; migrated: boolean } {
    if (!this.storage) return { profile, migrated: false };
    const patch: {
      musicVolume?: number;
      sfxVolume?: number;
      mute?: boolean;
    } = {};
    const music = this.storage.getItem(LEGACY_MUSIC_VOLUME_KEY);
    const sfx = this.storage.getItem(LEGACY_SFX_VOLUME_KEY);
    const mute = this.storage.getItem(LEGACY_MUTE_KEY);
    if (music !== null) patch.musicVolume = Number(music);
    if (sfx !== null) patch.sfxVolume = Number(sfx);
    if (mute !== null) patch.mute = mute === '1' || mute === 'true';
    if (Object.keys(patch).length === 0) return { profile, migrated: false };
    this.logger.info('migrateLegacyAudioSettings:applied', { keys: Object.keys(patch) });
    return { profile: updateProfileSettings(profile, patch, this.logger), migrated: true };
  }

  private tryWrite(profile: PlayerProfile, operation: string): boolean {
    if (!this.storage) return false;
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(profile));
      return true;
    } catch (error) {
      this.logger.error('tryWrite:write-failed', { storageKey: this.storageKey, operation, error });
      return false;
    }
  }

  private clearLegacyAudioSettings(operation: string): void {
    if (!this.storage) return;
    try {
      this.storage.removeItem(LEGACY_MUSIC_VOLUME_KEY);
      this.storage.removeItem(LEGACY_SFX_VOLUME_KEY);
      this.storage.removeItem(LEGACY_MUTE_KEY);
      this.logger.debug('clearLegacyAudioSettings:complete', { storageKey: this.storageKey, operation });
    } catch (error) {
      this.logger.warn('clearLegacyAudioSettings:failed', { storageKey: this.storageKey, operation, error });
    }
  }
}

function resolveLocalStorage(): ProfileStorageLike | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}
